import { ApolloClient, InMemoryCache } from "@apollo/client";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import Link from "next/link";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import Navbar from "../../components/Nav/Nav";
import NavHook from "../../components/Nav/NavHook";
import Pagination from "../../components/Pagination/Pagination";
import Recipe from "../../components/Recipe/Recipe";
import RecipeSearchBar from "../../components/RecipeSearchBar/RecipeSearchBar";
import {
  GET_ALL_DIET_TAGS,
  GET_ALL_RECIPES,
  GET_ALL_SEASON_TAGS,
} from "../../graphql/recipes";
import { timeConvert } from "../../utils/helpers";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import RefreshedFooter from "../../components/RefreshedFooter/RefreshedFooter";
import RecipeDifficultyFilterTag from "../../components/RecipeDifficultyFilterTag/RecipeDifficultyFilterTag";
import DietTagFilter from "../../components/DietTagFilter/DietTagFilter";
import SeasonTagFilter from "../../components/SeasonTagFilter/SeasonTagFilter";

export default function Recipes({
  recipes,
  seasonTags,
  dietTags,
  navigation,
  footer,
}) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [seasonFilter, setSeasonFilter] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [dietFilter, setDietFilter] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [locale, setLocale] = useState("");
  const router = useRouter();
  const dietParams = dietFilter?.map((d) => d);

  const handleClickDifficultyFilter = (difficulty) => {
    switch (difficulty) {
      case 1:
        if(difficultyFilter === "facile") {
          setDifficultyFilter("")
          break;
        }
        setDifficultyFilter("facile")
        break;
      case 2:
        if(difficultyFilter === "moyen") {
          setDifficultyFilter("")
          break;
        }
        setDifficultyFilter("moyen")
        break;
      case 3:
        if(difficultyFilter === "difficile") {
          setDifficultyFilter("")
          break;
        }
        setDifficultyFilter("difficile")
        break;
    }
  }

  useEffect(() => {
    router.push({
      pathname: "/recipes",
      query: {
        page: router.query.page && !searchTerm ? router.query.page : 1,
        search: searchTerm ? searchTerm : [],
        seasonFilter: seasonFilter ? seasonFilter : [],
        dietFilter: dietParams ? dietParams : [],
        difficultyFilter: difficultyFilter ? difficultyFilter : []
      },
    });
  }, [searchTerm, seasonFilter, dietFilter, difficultyFilter]);

  useEffect(() => {
    window?.rudderanalytics?.page();
  }, []);

  useEffect(() => {
    const topLevelDomain = window.location.hostname.split(".").pop();
    setLocale(topLevelDomain === "es" ? "es" : "fr");
  }, []);

  return (
    <>
      <Head>
        <title>Bene Bono</title>
        <meta
          property="og:title"
          content="Bene Bono | Cestas Ecológicas y Anti Desperdicio"
        />
        <meta
          property="og:site_name"
          content="Bene Bono"
        />
        <meta
          property="og:url"
          content={`benebono.${locale}`}
        />
        <meta
          property="og:description"
          content=""
        />
        <meta
          property="og:type"
          content="website"
        />
        <meta
          property="og:image"
          content="https://img.plasmic.app/img-optimizer/v1/img/c44d43862fdbf580d7139ef62c2c6ffa.png?q=75&f=webp"
        />
        {process.env.NODE_ENV !== "production" && (
          <meta
            name="robots"
            content="index,follow"
          />
        )}
      </Head>
      <Navbar links={navigation.links} />
      <div className="recipes_container">
        <div className="recipe_header_container">
          <h1>{t("recipes.title")}</h1>
          <RecipeSearchBar
            setSearchTerm={setSearchTerm}
          />
          <div className="recipe_header_filter">
            <div className="recipe_header_filter_difficulty">
              <p className="recipe_header_filter_title">{t("filter.difficulty")} :</p>
              <div className="recipe_header_filter_difficulty_tags">
                <RecipeDifficultyFilterTag handleClickDifficultyFilter={handleClickDifficultyFilter} difficulty={1} difficultyFilter={difficultyFilter === "facile" ? true : false} />
                <RecipeDifficultyFilterTag handleClickDifficultyFilter={handleClickDifficultyFilter} difficulty={2} difficultyFilter={difficultyFilter === "moyen" ? true : false} />
                <RecipeDifficultyFilterTag handleClickDifficultyFilter={handleClickDifficultyFilter} difficulty={3} difficultyFilter={difficultyFilter === "difficile" ? true : false} />
              </div>
            </div>
            <div className="recipe_header_filter_diet">
              <p className="recipe_header_filter_title">{t("filter.diet")} :</p>
              <div className="recipe_header_filter_diet_tags">
                {dietTags.data.map((t) => {
                  const name = t.attributes.name;
                  const selected = dietFilter.includes(name);

                  return (
                    <DietTagFilter
                      key={name}
                      tag={name}
                      selected={selected}
                      setDietFilter={setDietFilter}
                    />
                  );
                })}
              </div>
            </div>
            <div className="recipe_header_filter_season">
              <p className="recipe_header_filter_title">{t("filter.season")} :</p>
              <div className="recipe_header_filter_season_tags">
                {seasonTags.data.map((t) => {
                  const name = t.attributes.name;
                  const selected = seasonFilter === name;

                  return (
                    <SeasonTagFilter
                      key={name}
                      tag={name}
                      selected={selected}
                      setSeasonFilter={setSeasonFilter}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="recipes_content">
          <div
            className="recipes"
            onClick={() => setShowFilters((prev) => !prev)}
          >
            {recipes.data.map((r, i) => {
              return (
                <Link
                  key={i}
                  href={`/recipes/${r.attributes.slug}`}
                >
                  <Recipe
                    imageUrl={
                      r.attributes?.preview_image?.data[0]?.attributes?.url
                        ? `${r.attributes?.preview_image?.data[0]?.attributes?.url}`
                        : "https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=774&q=80"
                    }
                    tags={r.attributes.season_tags.data}
                    title={r.attributes.title}
                    difficulty={
                      r?.attributes.recipe_difficulty?.data[0]?.attributes?.name
                    }
                    duration={
                      r?.attributes.duration < 60
                        ? `${r?.attributes.duration}min`
                        : timeConvert(r?.attributes.duration)
                    }
                    regimeTags={r.attributes.dietTag.data}
                  />
                </Link>
              );
            })}
          </div>
        </div>

        <Pagination
          link="/recipes"
          pageCount={recipes.meta.pagination.pageCount}
        />
      </div>
      <RefreshedFooter links={footer.links} />
    </>
  );
}

export async function getServerSideProps({ query, locale }) {
  const client = new ApolloClient({
    uri: process.env.STRAPI_GRAPHQL_URL,
    cache: new InMemoryCache(),
  });

  const recipes = await client.query({
    query: GET_ALL_RECIPES,
    variables: {
      pageSize: 12,
      page: query.page ? parseInt(query.page) : 1,
      search: query.search ? query.search : "",
      seasonFilter: query.seasonFilter ? query.seasonFilter : undefined,
      dietFilter: query.dietFilter ? query.dietFilter : undefined,
      difficultyFilter: query.difficultyFilter,
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const seasonTags = await client.query({
    query: GET_ALL_SEASON_TAGS,
    variables: {
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const dietTags = await client.query({
    query: GET_ALL_DIET_TAGS,
    variables: {
      locale: locale === "es" ? locale : "fr-FR",
    },
  });

  const mainNav = await NavHook(locale, "main");
  const navigation = {
    links: mainNav,
  };

  const footerNav = await NavHook(locale, "footer");
  const footer = {
    links: footerNav,
  };

  return {
    props: {
      ...(await serverSideTranslations(locale)),
      recipes: recipes.data.recettes,
      seasonTags: seasonTags.data.seasonTags,
      dietTags: dietTags.data.dietTags,
      navigation,
      footer,
      locale,
    },
  };
}
